import { useMutation, useQuery } from '@apollo/client';
import type { UpdateQueryFn } from '@apollo/client/core/watchQueryOptions';
import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { Op } from 'quill/core';
import Quill from 'quill/core';
import type QuillHeader from 'quill/formats/header';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { SetNonNullable } from 'type-fest';
import { useDebouncedCallback } from 'use-debounce';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { useQuill } from '../editor/QuillContext';
import { getRangeSaver } from '../editor/RangeSaver';
import { useUIElementResizeListener } from '../editor/UIElementResizeContext';
import type {
  MagneticWallFocus,
  MagneticWallType,
} from '../editor/editorSlice';
import { CREATION_ID, EditorActions } from '../editor/editorSlice';
import AnnotationAttributor from '../editor/formats/Annotation';
import Comment from '../editor/formats/Comment';
import Reaction from '../editor/formats/Reaction';
import {
  getAttributorType,
  getClosestAttribute,
} from '../editor/formats/utils/attributors';
import { getHeaderCollapse } from '../editor/useHeaderCollapse';
import getRangeBoundaries from '../editor/utils/getRangeBoundaries';
import COMMENT_REACTION_CREATED from '../graphql/commentReactionCreated';
import COMMENT_REACTION_DELETED from '../graphql/commentReactionDeleted';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type { PostCommentThreadsQuery } from '../graphql/generated/graphql';
import THREAD_UNRESOLVED_SUBSCRIPTION from '../graphql/threadUnresolved';
import UNRESOLVE_THREAD_MUTATION from '../graphql/unresolveThread';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import addTestId from '../utils/addTestId';
import updateApolloList from '../utils/graphql/updateApolloList';
import { legacyUpdateQueryList } from '../utils/graphql/updateQueryList';
import updateSubscribedList from '../utils/graphql/updateSubscribedList';
import { getAnnotationIdFromUrlHash } from '../utils/url/annotationThreadHash';
import { getThreadIdFromUrlHash } from '../utils/url/commentThreadHash';
import { getReactionIdFromUrlHash } from '../utils/url/reactionThreadHash';
import useIsPublicView from '../utils/useIsPublicView';
import HoverContentPreview from './HoverContentPreview';
import * as styles from './MagneticWall.module.scss';
import type { MagneticWallItemSize } from './MagneticWallItem';
import { usePostLayoutState } from './PostLayoutProvider';
import Annotation from './annotation/Annotation';
import AnnotationSheet from './annotation/AnnotationSheet';
import CommentThread from './comment/CommentThread';
import CommentThreadSheet from './comment/CommentThreadSheet';
import ReactionThread from './comment/ReactionThread';
import ReactionThreadSheet from './comment/ReactionThreadSheet';
import type {
  Layout,
  MagneticWallState,
} from './comment/getMagneticWallItemLayout';
import getMagneticWallItemLayout, {
  HeightState,
  VerticalSpan,
} from './comment/getMagneticWallItemLayout';
import showFullPageSheet from './comment/showFullPageSheet';
import {
  getScrollContainer,
  useListenToPostScroll,
} from './listenToPostScroll';
import useMagneticWallItemSize from './useMagneticWallItemSize';
import { MainEditorResizeHandler } from './useOnMainEditorResize';
import useVisibleMagneticWallItems from './useVisibleMagneticWallItems';
import type { DisplayId } from './utils/toDisplayId';
import toDisplayId from './utils/toDisplayId';

const MAGNETIC_WALL_MAX_WIDTH = parseInt(styles.magneticWallMaxWidth, 10);

const POST_FRAGMENT = gql(`
  fragment MagneticWall_post on PublicPostFields {
    id
    ...CommentThread_post
    ...ReactionThread_post
    ...CommentThreadSheet_post
    ...Annotation_post
  }
`);

const MAGNETIC_WALL_THREADS_FRAGMENT = gql(`
  fragment MagneticWall_threads on PublicThreadFields {
    id
    ...CommentThread_thread
    ...ReactionThread_thread
    ...CommentThreadSheet_thread
    ...ReactionThreadSheet_thread
  }
`);

const MAGNETIC_WALL_ANNOTATIONS_FRAGMENT = gql(`
  fragment MagneticWall_annotations on PublicAnnotationFields {
    id
    ...Annotation_annotation
  }
`);

export const POST_THREADS_QUERY = gql(/** @complexity 47 */ `
  query postCommentThreads($postId: ID!) {
    post(id: $postId) {
      id
      threads {
        id
        ...MagneticWall_threads
      }
      annotations {
        ...MagneticWall_annotations
      }
    }
  }
`);

const PUBLIC_POST_THREADS_QUERY = gql(/** @complexity 47 */ `
  query publicPostCommentThreads($postId: ID!) {
    post: publicPost(id: $postId) {
      id
      threads {
        id
        ...MagneticWall_threads
      }
      annotations {
        ...MagneticWall_annotations
      }
    }
  }
`);

const CREATE_REACTION_MUTATION = gql(/** @complexity 44 */ `
  mutation createReaction(
    $threadId: ID
    $postId: ID!
    $version: Int!
    $checksum: Int!
    $mark: Json!
    $emoji: String!
  ) {
    createReaction(
      threadId: $threadId
      postId: $postId
      version: $version
      checksum: $checksum
      mark: $mark
      emoji: $emoji
    ) {
      ...MagneticWall_threads
    }
  }
`);

const ANNOTATION_CREATED_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription annotationCreated($postId: ID!) {
    annotation: annotationCreated(postId: $postId) {
      ...MagneticWall_annotations
    }
  }
`);

const ANNOTATION_DELETED_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription annotationDeleted($postId: ID!) {
    annotation: annotationDeleted(postId: $postId) {
      id
    }
  }
`);

const ANNOTATION_UPDATED_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription annotationUpdated($postId: ID!) {
    annotation: annotationUpdated(postId: $postId) {
      ...MagneticWall_annotations
    }
  }
`);

const THREAD_CREATED_SUBSCRIPTION = gql(/** @complexity 88 */ `
  subscription threadCreated($postId: ID!) {
    thread: threadCreated(postId: $postId) {
      ...MagneticWall_threads
    }
  }
`);

const THREAD_DELETED_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription threadDeleted($postId: ID!) {
    thread: threadDeleted(postId: $postId) {
      id
    }
  }
`);

const THREAD_RESOLVED_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription threadResolved($postId: ID!) {
    thread: threadResolved(postId: $postId) {
      id
    }
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const THREAD_REPLIED_SUBSCRIPTION = gql(/** @complexity 32 */ `
  subscription threadReplied($postId: ID!) {
    comment: threadReplied(postId: $postId) {
      ...CommentFields
      thread {
        id
      }
    }
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const THREAD_REACTED_SUBSCRIPTION = gql(/** @complexity 16 */ `
  subscription threadReacted($postId: ID!) {
    reaction: threadReacted(postId: $postId) {
      ...ReactionFields
      thread {
        id
      }
    }
  }
`);

const COMMENT_DELETED_SUBSCRIPTION = gql(/** @complexity 8 */ `
  subscription commentDeleted($postId: ID!) {
    comment: commentDeleted(postId: $postId) {
      id
      thread {
        id
      }
    }
  }
`);

const COMMENT_UPDATED_SUBSCRIPTION = gql(/** @complexity 32 */ `
  subscription commentUpdated($postId: ID!) {
    comment: commentUpdated(postId: $postId) {
      ...CommentFields
      thread {
        id
      }
    }
  }
`);

const REACTION_DELETED_SUBSCRIPTION = gql(/** @complexity 8 */ `
  subscription reactionDeleted($postId: ID!) {
    reaction: reactionDeleted(postId: $postId) {
      id
      thread {
        id
      }
    }
  }
`);

const updateWithinThread = <Type extends 'comment' | 'reaction'>(
  prev: PostCommentThreadsQuery,
  result: {
    subscriptionData: {
      data: {
        [x in Type]?:
          | {
              id?: string | null | undefined;
              thread: { id: string };
            }
          | null
          | undefined;
      };
    };
  },
  type: Type,
  add: boolean,
) => {
  const { data } = result.subscriptionData;
  if (!prev || !data?.[type]?.thread) {
    return prev;
  }
  const types = `${type}s` as const;
  return {
    ...prev,
    post: {
      ...prev.post,
      threads: prev.post.threads.map((thread) => {
        if (thread.id !== data?.[type]?.thread.id) {
          return thread;
        }
        return updateApolloList(
          thread,
          data,
          types,
          type as Exclude<Extract<Type, string>, '__typename'>,
          add,
        );
      }),
    },
  };
};

type UpdateCommentSubscriptionData = {
  commentReaction?: {
    id?: string;
    threadId: string;
    commentId: string;
  } | null;
};

const updateCommentReactions =
  (
    add: boolean,
  ): UpdateQueryFn<
    PostCommentThreadsQuery,
    { postId: string },
    UpdateCommentSubscriptionData
  > =>
  (prev, { subscriptionData: { data } }) =>
    !prev || !data?.commentReaction
      ? prev
      : {
          ...prev,
          post: prev.post && {
            ...prev.post,
            threads: prev.post.threads?.map((thread) =>
              thread.id !== data.commentReaction?.threadId
                ? thread
                : {
                    ...thread,
                    comments: thread.comments?.map((comment) =>
                      comment.id !== data.commentReaction?.commentId
                        ? comment
                        : updateApolloList(
                            comment,
                            data,
                            'reactions',
                            'commentReaction',
                            add,
                          ),
                    ),
                  },
            ),
          },
        };

const useMagneticWallItems = (
  postId: string,
  { subscribe } = { subscribe: false },
) => {
  const isPublicView = useIsPublicView();

  const { data, subscribeToMore } = useQuery(
    isPublicView ? PUBLIC_POST_THREADS_QUERY : POST_THREADS_QUERY,
    {
      variables: { postId },
    },
  );

  const subscribeThreads = useCallback(() => {
    const subscriptions = [
      subscribeToMore({
        document: THREAD_CREATED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList('post.threads', 'thread', true),
      }),
      subscribeToMore({
        document: THREAD_DELETED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList('post.threads', 'thread', false),
      }),
      subscribeToMore({
        document: THREAD_UNRESOLVED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList('post.threads', 'thread', true),
      }),
      subscribeToMore({
        document: THREAD_RESOLVED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList('post.threads', 'thread', false),
      }),
      subscribeToMore({
        document: ANNOTATION_CREATED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList(
          'post.annotations',
          'annotation',
          true,
        ),
      }),
      subscribeToMore({
        document: ANNOTATION_DELETED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList(
          'post.annotations',
          'annotation',
          false,
        ),
      }),
      subscribeToMore({
        document: ANNOTATION_UPDATED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: updateSubscribedList(
          'post.annotations',
          'annotation',
          true,
        ),
      }),
      subscribeToMore({
        document: THREAD_REPLIED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: (prev, result) =>
          updateWithinThread(prev, result, 'comment', true),
      }),
      subscribeToMore({
        document: THREAD_REACTED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: (prev, result) =>
          updateWithinThread(prev, result, 'reaction', true),
      }),
      subscribeToMore({
        document: COMMENT_DELETED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: (prev, result) =>
          updateWithinThread(prev, result, 'comment', false),
      }),
      subscribeToMore({
        document: COMMENT_UPDATED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: (prev, result) =>
          updateWithinThread(prev, result, 'comment', true),
      }),
      subscribeToMore({
        document: REACTION_DELETED_SUBSCRIPTION,
        variables: { postId },
        updateQuery: (prev, result) =>
          updateWithinThread(prev, result, 'reaction', false),
      }),
      subscribeToMore({
        document: COMMENT_REACTION_CREATED,
        variables: { postId },
        updateQuery: updateCommentReactions(true),
      }),
      subscribeToMore({
        document: COMMENT_REACTION_DELETED,
        variables: { postId },
        updateQuery: updateCommentReactions(false),
      }),
    ];

    return () => {
      subscriptions.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, [postId, subscribeToMore]);

  useEffect(() => {
    if (subscribe) {
      return subscribeThreads();
    }

    return undefined;
  }, [subscribe, subscribeThreads]);

  return useMemo<{
    threads: Array<
      | DocumentType<typeof POST_THREADS_QUERY>['post']['threads'][number]
      | DocumentType<
          typeof PUBLIC_POST_THREADS_QUERY
        >['post']['threads'][number]
    >;
    annotations: Array<
      | DocumentType<typeof POST_THREADS_QUERY>['post']['annotations'][number]
      | DocumentType<
          typeof PUBLIC_POST_THREADS_QUERY
        >['post']['annotations'][number]
    >;
  }>(
    () => ({
      threads: data?.post?.threads ?? [],
      annotations: data?.post.annotations ?? [],
    }),
    [data],
  );
};

export const useCreateReaction = () => {
  const [createReaction] = useMutation(CREATE_REACTION_MUTATION);

  return (
    postId: string,
    version: number,
    checksum: number,
    mark: string,
    emoji: string,
  ) => {
    return createReaction({
      variables: { postId, version, checksum, mark, emoji },
      update: legacyUpdateQueryList(
        { query: POST_THREADS_QUERY, variables: { postId } },
        'post.threads',
        'createReaction',
      ),
    });
  };
};

interface MagneticWallItemContainerProps {
  children: React.ReactNode;
  focused: boolean;
  top: number;
  displayId: DisplayId;
}

const MagneticWallItemContainer = ({
  children,
  top,
  focused,
  displayId,
}: MagneticWallItemContainerProps) => {
  return (
    <div
      data-display-id={displayId}
      className={classNames('magnetic-wall-item-container', {
        'magnetic-wall-item-container-focused': focused,
      })}
      style={{ top }}
    >
      {children}
    </div>
  );
};

type MagneticWallDisplayItemWithPosition = MagneticWallDisplayItem & {
  top: number;
  isOverlapped: boolean;
};
interface BaseMagneticWallProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  magneticWallFocus: MagneticWallFocus | null;
  onResize: (avoidRepositioningFocusedThread?: boolean) => void;
  visibleMagneticWallDisplayItems: MagneticWallDisplayItemWithPosition[];
  focusedDisplayItem: MagneticWallDisplayItem | null;
  canEditPost: boolean;
  magneticWallItemSize: MagneticWallItemSize;
}

const BaseMagneticWall = ({
  post,
  magneticWallFocus,
  onResize,
  visibleMagneticWallDisplayItems,
  focusedDisplayItem,
  canEditPost,
  magneticWallItemSize,
}: BaseMagneticWallProps) => {
  const { canMagneticWallExpand } = usePostLayoutState();
  const dispatch = useAppDispatch();

  const { quill } = useQuill();

  const handleBlur = () => {
    dispatch(EditorActions.blurMagneticWall());
  };

  const [unsavedInputValue, setUnsavedInputValue] = useState<
    Record<DisplayId, Op[]>
  >({});

  const debouncedHandleInputChange = useDebouncedCallback(
    (displayId: DisplayId, value: Op[]) =>
      setUnsavedInputValue((unsaved) => ({ ...unsaved, [displayId]: value })),
    500,
  );

  const isShowingFullSheetItem = showFullPageSheet();

  const [unresolveThreadMutation] = useMutation(UNRESOLVE_THREAD_MUTATION);

  const unresolveThread = async (threadId: string) => {
    await unresolveThreadMutation({
      variables: { id: threadId },
      update: legacyUpdateQueryList(
        { query: POST_THREADS_QUERY, variables: { postId: post.id } },
        'post.threads',
        'unresolveThread',
        true,
      ),
    });
  };

  const shouldShowFullPageSheet = showFullPageSheet();

  const sortedMagneticWallDisplayItems = _.sortBy(
    visibleMagneticWallDisplayItems,
    (item) => -item.top,
  );

  return (
    <>
      <ClickOutsideHandler
        onClickOutside={(e) => {
          const { target } = e;

          if (
            isShowingFullSheetItem ||
            // The editor is handling the blurring by itself
            (target instanceof Element && quill?.root.contains(target))
          ) {
            return;
          }

          handleBlur();
        }}
      >
        <HoverContentPreview>
          {sortedMagneticWallDisplayItems.map((item) => {
            const {
              data: { id },
              displayId,
              type,
              top,
            } = item;

            const isFocused =
              magneticWallFocus?.id === id &&
              (magneticWallFocus.type === type ||
                (focusedDisplayItem == null &&
                  magneticWallFocus.type === 'comment'));

            const hasExpandIntent =
              isFocused &&
              magneticWallFocus.hasExpandIntent &&
              canMagneticWallExpand;

            const isPopOverOpen =
              isFocused &&
              Boolean(magneticWallFocus.active) &&
              !hasExpandIntent;

            const unsavedInputValueForItem = unsavedInputValue[displayId];

            return (
              <MagneticWallItemContainer
                key={displayId}
                displayId={displayId}
                focused={!shouldShowFullPageSheet && isFocused}
                top={top}
              >
                {item.type === 'annotation' && (
                  <Annotation
                    size={magneticWallItemSize}
                    annotation={item.data}
                    post={post}
                    unsavedInputValue={unsavedInputValueForItem}
                    active={isFocused}
                    popOverOpen={isPopOverOpen}
                    overlapped={item.isOverlapped}
                    onResize={onResize}
                    onInputChange={(ops) => {
                      debouncedHandleInputChange(displayId, ops);
                      onResize(true);
                    }}
                    canEdit={canEditPost}
                  />
                )}
                {item.type === 'reaction' && (
                  <ReactionThread
                    size={magneticWallItemSize}
                    thread={item.data}
                    isFocused={isFocused}
                    isPopOverOpen={isPopOverOpen}
                    onResize={onResize}
                    post={post}
                    overlapped={item.isOverlapped}
                    unresolveThread={unresolveThread}
                  />
                )}
                {item.type === 'comment' && (
                  <CommentThread
                    size={magneticWallItemSize}
                    thread={item.data}
                    post={post}
                    unsavedInputValue={unsavedInputValueForItem}
                    isFocused={isFocused}
                    isPopOverOpen={isPopOverOpen}
                    overlapped={item.isOverlapped}
                    onResize={onResize}
                    onInputChange={(ops) => {
                      debouncedHandleInputChange(displayId, ops);
                    }}
                    unresolveThread={unresolveThread}
                  />
                )}
              </MagneticWallItemContainer>
            );
          })}
        </HoverContentPreview>
      </ClickOutsideHandler>

      {isShowingFullSheetItem &&
        focusedDisplayItem &&
        magneticWallFocus?.active && (
          <>
            {focusedDisplayItem.type === 'comment' && (
              <CommentThreadSheet
                post={post}
                thread={focusedDisplayItem.data}
                onClose={() => dispatch(EditorActions.blurMagneticWall())}
                onInputChange={(ops) => {
                  debouncedHandleInputChange(focusedDisplayItem.displayId, ops);
                }}
                unsavedInputValue={
                  unsavedInputValue[focusedDisplayItem.displayId]
                }
              />
            )}
            {focusedDisplayItem.type === 'reaction' && (
              <ReactionThreadSheet
                thread={focusedDisplayItem.data}
                onClose={() => dispatch(EditorActions.blurMagneticWall())}
              />
            )}
            {focusedDisplayItem.type === 'annotation' && (
              <AnnotationSheet
                postId={post.id}
                annotation={focusedDisplayItem.data}
                onClose={() => dispatch(EditorActions.blurMagneticWall())}
                onInputChange={(ops) =>
                  debouncedHandleInputChange(focusedDisplayItem.displayId, ops)
                }
                canEdit={canEditPost}
                unsavedInputValue={
                  unsavedInputValue[focusedDisplayItem.displayId] ?? []
                }
              />
            )}
          </>
        )}
    </>
  );
};

const getReversedPrecedingHeaders = (
  node: HTMLElement,
  headers: QuillHeader[],
) =>
  headers
    .filter(
      (header) =>
        node.compareDocumentPosition(header.domNode) & // eslint-disable-line no-bitwise
        Node.DOCUMENT_POSITION_PRECEDING,
    )
    .toReversed();

function getRelativeDOMRect(rect: DOMRect, containerRect: DOMRect) {
  return DOMRect.fromRect({
    x: rect.x - containerRect.x,
    y: rect.y - containerRect.y,
    width: rect.width,
    height: rect.height,
  });
}

const getAbsoluteContentRect = (
  contentNode: HTMLElement,
  getVisibleCollapsedHeaders: () => QuillHeader[],
): DOMRect | null => {
  const isWithinCollapsedHeader = !contentNode.offsetParent;

  if (isWithinCollapsedHeader) {
    const visibleCollapsedHeaders = getVisibleCollapsedHeaders();

    const header = getReversedPrecedingHeaders(
      contentNode,
      visibleCollapsedHeaders,
    )[0];

    if (header) {
      return header.domNode.getBoundingClientRect();
    }
  }

  return contentNode.getClientRects()[0] ?? null;
};

interface MagneticWallProps {
  quill: Quill;
  post: DocumentType<typeof POST_FRAGMENT>;
  canEditPost: boolean;
  innerBannerRef: React.RefObject<HTMLElement>;
  hasBanner: boolean;
}

function queryNodesWithPrefixedIds({
  container,
  mapIds,
  prefix,
}: {
  container: HTMLElement;
  mapIds(node: HTMLElement): string[];
  prefix: string;
}) {
  const selector = `[class^="${prefix}"], [class*=" ${prefix}"]`;

  return Array.from(container.querySelectorAll<HTMLElement>(selector))
    .map((node) => ({ node, ids: mapIds(node) }))
    .filter(({ ids }) => ids.length);
}

export function getMagneticWallItemElementsWithIds(container: HTMLElement) {
  const comments = queryNodesWithPrefixedIds({
    container,
    mapIds: (node) => Comment.value(node) ?? [],
    prefix: `${Comment.keyName}-`,
  });

  const reactions = queryNodesWithPrefixedIds({
    container,
    mapIds: (node) => Reaction.value(node) ?? [],
    prefix: `${Reaction.keyName}-`,
  });

  const annotations = queryNodesWithPrefixedIds({
    container,
    mapIds: (node) => AnnotationAttributor.value(node) ?? [],
    prefix: `${AnnotationAttributor.keyName}-`,
  });

  return [...comments, ...annotations, ...reactions];
}

export function getAbsoluteContentRectsMap(
  container: HTMLElement,
  getVisibleCollapsedHeaders: () => QuillHeader[],
) {
  return getMagneticWallItemElementsWithIds(container)
    .flatMap(({ node, ids }) => {
      const contentRect = getAbsoluteContentRect(
        node,
        getVisibleCollapsedHeaders,
      );
      return contentRect ? ids.map((id) => ({ id, contentRect })) : [];
    })
    .reduce(
      (memo, { id, contentRect }) => {
        const existing = memo[id];
        if (existing != null) {
          const top = Math.min(contentRect.top, existing.top);
          return {
            ...memo,
            [id]: DOMRect.fromRect({
              x: existing.x,
              y: top,
              height: Math.max(contentRect.bottom, existing.bottom) - top,
              width: existing.width,
            }),
          };
        }
        return {
          ...memo,
          [id]: contentRect,
        };
      },
      {} as Record<string, DOMRect>,
    );
}

type MagneticWallDisplayItem = { displayId: DisplayId } & (
  | {
      type: 'annotation';
      data: DocumentType<typeof MAGNETIC_WALL_ANNOTATIONS_FRAGMENT>;
    }
  | {
      type: 'reaction' | 'comment';
      data: DocumentType<typeof MAGNETIC_WALL_THREADS_FRAGMENT>;
    }
);

const useSelectionChangeHandler = (handler: (range: Range) => void) => {
  const lastRangeRef = useRef<Range | null>(null);
  const handlerRef = useRef<(range: Range) => void>(handler);
  useLayoutEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    const listener = () => {
      const selection = document.getSelection();
      if (!selection?.rangeCount) return;

      const range = selection.getRangeAt(0);
      const lastRange = lastRangeRef.current;
      if (
        lastRange &&
        lastRange.startContainer === range.startContainer &&
        lastRange.startOffset === range.startOffset &&
        lastRange.endContainer === range.endContainer &&
        lastRange.endOffset === range.endOffset
      ) {
        return;
      }
      lastRangeRef.current = range;
      handlerRef.current(range);
    };
    document.addEventListener('selectionchange', listener);
    return () => {
      document.removeEventListener('selectionchange', listener);
    };
  }, []);
};

function parseHash(
  hash: string,
): { type: MagneticWallType; id: string } | null {
  const threadId = getThreadIdFromUrlHash(hash);
  if (threadId) {
    return { type: 'comment', id: threadId };
  }

  const annotationId = getAnnotationIdFromUrlHash(hash);
  if (annotationId) {
    return { type: 'annotation', id: annotationId };
  }

  const reactionId = getReactionIdFromUrlHash(hash);
  if (reactionId) {
    return { type: 'reaction', id: reactionId };
  }

  return null;
}

function useScrollToThreadAnchor({ skip }: { skip: boolean }) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { hash } = location;

  useEffect(() => {
    if (skip) return;

    const parseResult = parseHash(hash);
    if (!parseResult) return;

    dispatch(
      EditorActions.focusMagneticWall({
        id: parseResult.id,
        type: parseResult.type,
        isActive: true,
        hasExpandIntent: parseResult.type === 'comment',
      }),
    );

    const displayId: DisplayId = `${parseResult.type}-${parseResult.id}`;
    const selector = `[data-display-id="${displayId}"]`;

    const containerElement =
      document
        .querySelector(selector)
        ?.querySelector(
          '.magnetic-wall-item-full-container, .magnetic-wall-item-compact-container',
        ) ?? document.querySelector(selector);

    if (containerElement instanceof HTMLElement) {
      containerElement.scrollIntoView();
    }
  }, [dispatch, skip, hash]);
}

enum DEBOUNCE_DELAY {
  Fast = 100,
  Slow = 1000,
}

interface useUpdateThreadHeightsArgs {
  stateRef: React.MutableRefObject<MagneticWallState>;
  /**
   * @param avoidRepositioningFocusedThread - If true, the focused thread will not be repositioned.
   * This is needed because in some cases we don't want to reposition the focused thread to avoid interrupting the user. E.g. when user is typing in the comment input.
   */
  updatePositions: (avoidRepositioningFocusedThread?: boolean) => void;
}

const useUpdateThreadHeights = ({
  stateRef,
  updatePositions,
}: useUpdateThreadHeightsArgs) => {
  const avoidRepositioningFocusedThreadRef = useRef<boolean | null>(null);

  const debouncedHandleThreadResize = useDebouncedCallback(() => {
    const shouldAvoid = avoidRepositioningFocusedThreadRef.current ?? false;
    avoidRepositioningFocusedThreadRef.current = null;

    const { magneticWallItems, magneticWallFocus, heights } = stateRef.current;
    if (!magneticWallItems.length) return;

    const activeDisplayId = magneticWallFocus
      ? toDisplayId(magneticWallFocus)
      : null;
    magneticWallItems.forEach((item) => {
      const node = document.querySelector(
        `[data-display-id="${item.displayId}"]`,
      );

      const existing = heights[item.displayId] ?? {
        [HeightState.Focus]: null,
        [HeightState.Normal]: null,
      };

      const height = node instanceof HTMLElement ? node.offsetHeight : null;
      if (activeDisplayId === item.displayId) {
        existing[HeightState.Focus] = height;
      } else {
        existing[HeightState.Normal] = height;

        // Set the focus height if it hasn't been set yet to improve animations.
        // Future: This could be even improved by estimating the height based on the content.
        if (existing[HeightState.Focus] == null) {
          existing[HeightState.Focus] = height;
        }
      }
      heights[item.displayId] = existing;
    });

    updatePositions(shouldAvoid);
  }, DEBOUNCE_DELAY.Fast);

  return useCallback(
    (avoidRepositioningFocusedThread = false) => {
      // Falsy values will take priority over truthy values.
      if (!avoidRepositioningFocusedThread) {
        avoidRepositioningFocusedThreadRef.current = false;
      } else if (avoidRepositioningFocusedThreadRef.current == null) {
        avoidRepositioningFocusedThreadRef.current = true;
      }

      debouncedHandleThreadResize();
    },
    [debouncedHandleThreadResize],
  );
};

const MagneticWall = ({
  quill,
  post,
  canEditPost,
  innerBannerRef,
  hasBanner,
}: MagneticWallProps) => {
  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );

  const currentUser = useAppSelector((state) => state.session.user);

  const { threads, annotations } = useMagneticWallItems(post.id, {
    subscribe: true,
  });

  const magneticWallDisplayItems: MagneticWallDisplayItem[] = useMemo(() => {
    const isCreating = magneticWallFocus?.id === CREATION_ID;
    const reactions = threads.filter((thread) => thread.reactions.length > 0);
    const commentThreads = threads.filter(
      (thread) => thread.comments.length > 0,
    );

    return [
      ...annotations.map((data) => ({
        type: 'annotation' as const,
        data,
      })),
      ...commentThreads.map((data) => ({
        type: 'comment' as const,
        data: { ...data, reactions: [] },
      })),
      ...reactions.map((data) => ({
        type: 'reaction' as const,
        data: { ...data, comments: [] },
      })),
      ...(isCreating
        ? [
            magneticWallFocus.type === 'annotation'
              ? {
                  data: {
                    __typename: 'Annotation' as const,
                    id: CREATION_ID,
                    author: currentUser,
                    content: [],
                  },
                  type: magneticWallFocus.type,
                }
              : {
                  data: {
                    __typename: 'Thread' as const,
                    id: CREATION_ID,
                    author: currentUser,
                    comments: [],
                    reactions: [],
                    content: [],
                  },
                  type: magneticWallFocus.type,
                },
          ]
        : []),
    ].map((item) => ({
      ...item,
      displayId: toDisplayId({ id: item.data.id, type: item.type }),
    }));
  }, [annotations, currentUser, magneticWallFocus, threads]);

  const dispatch = useAppDispatch();
  const addUIElementResizeListener = useUIElementResizeListener();

  const containerRef = useRef<HTMLDivElement>(null);

  const magneticWallItemSize = useMagneticWallItemSize(containerRef);

  const stateRef = useRef<MagneticWallState>({
    contentRects: {},
    heights: {},
    restrictedSpans: [],
    magneticWallItems: magneticWallDisplayItems,
    magneticWallItemSize,
    magneticWallFocus,
    scrollingContainerSpan: new VerticalSpan(-Infinity, Infinity),
  });
  const [layout, setLayout] = useState<
    Layout & {
      focusedThreadDisplayId: string | null;
    }
  >(() => ({
    tops: {},
    magneticWallMinHeight: null,
    overlappedDisplayIds: new Set(),
    focusedThreadDisplayId: null,
  }));

  const updateLayout = useCallback(
    (avoidRepositioningFocusedThread = false) => {
      setLayout((prev) => {
        const state = stateRef.current;
        const focusedThreadDisplayId = state.magneticWallFocus
          ? toDisplayId(state.magneticWallFocus)
          : null;

        const previousTop =
          focusedThreadDisplayId && prev.tops[focusedThreadDisplayId];

        const shouldReserve =
          avoidRepositioningFocusedThread &&
          focusedThreadDisplayId &&
          typeof previousTop === 'number' &&
          focusedThreadDisplayId === prev.focusedThreadDisplayId;

        const next = {
          ...getMagneticWallItemLayout(
            state,
            shouldReserve
              ? { displayId: focusedThreadDisplayId, top: previousTop }
              : undefined,
          ),
          focusedThreadDisplayId,
        };
        return _.isEqual(prev, next) ? prev : next;
      });
    },
    [],
  );

  const updateScrollingContainerVerticalBounds = useCallback(() => {
    const scrollingContainer = getScrollContainer();
    if (scrollingContainer) {
      stateRef.current.scrollingContainerSpan = VerticalSpan.fromDOMRect(
        getRelativeDOMRect(
          scrollingContainer.getBoundingClientRect(),
          quill.container.getBoundingClientRect(),
        ),
      );
    }
  }, [quill]);

  const visibleMagneticWallDisplayItems = magneticWallDisplayItems
    .map((item) => ({
      ...item,
      top: layout.tops[item.displayId],
      isOverlapped: layout.overlappedDisplayIds.has(item.displayId),
    }))
    .filter(
      (item): item is SetNonNullable<typeof item, 'top'> => item.top != null,
    );

  const visibleDisplayIds = new Set(
    visibleMagneticWallDisplayItems.map((item) => item.displayId),
  );
  useVisibleMagneticWallItems(visibleDisplayIds);

  const debouncedUpdateScrollingContainerVerticalBounds = useDebouncedCallback(
    updateScrollingContainerVerticalBounds,
    DEBOUNCE_DELAY.Fast,
  );

  const updateDesiredPositions = () => {
    if (!quill) return;
    const { container } = quill;

    const getVisibleCollapsedHeaders = _.memoize(() => {
      const collapsedHeaders =
        getHeaderCollapse(quill)?.getCollapsedHeaders() ?? [];
      return collapsedHeaders.filter((header) => !!header.domNode.offsetParent);
    });

    const containerRect = container.getBoundingClientRect();
    const newContentRects = _.mapValues(
      getAbsoluteContentRectsMap(container, getVisibleCollapsedHeaders),
      (rect) => getRelativeDOMRect(rect, containerRect),
    );

    if (magneticWallFocus?.id === CREATION_ID) {
      const boundaries = getRangeBoundaries(
        quill,
        getRangeSaver(quill).getLast(),
      );
      if (boundaries?.length) {
        const top = Math.min(...boundaries.map((rect) => rect.top));
        const bottom = Math.max(
          ...boundaries.map((rect) => rect.top + rect.height),
        );

        newContentRects[CREATION_ID] = DOMRect.fromRect({
          x: Math.min(...boundaries.map((rect) => rect.left)),
          y: top,
          height: bottom - top,
        });
      }
    }

    const widerLineRects = Array.from(
      quill.root.querySelectorAll(':scope > .span-full'),
    ).flatMap((node) => {
      if (!(node as HTMLElement).offsetParent) return [];

      const rect = node.getBoundingClientRect();
      return [
        {
          absolute: rect,
          relative: getRelativeDOMRect(rect, containerRect),
        },
      ];
    });

    const magneticWallRect = containerRef.current?.getBoundingClientRect();
    const magneticWallAbsoluteLeft = magneticWallRect
      ? magneticWallRect.right -
        Math.min(MAGNETIC_WALL_MAX_WIDTH, magneticWallRect?.width)
      : null;

    stateRef.current.contentRects = newContentRects;

    const intersectingWiderLineSpans = widerLineRects
      .filter(
        ({ absolute }) =>
          !magneticWallAbsoluteLeft ||
          absolute.right > magneticWallAbsoluteLeft,
      )
      .map(({ relative }) => VerticalSpan.fromDOMRect(relative));

    const bannerRect = innerBannerRef.current?.getBoundingClientRect();
    const isBannerIntersecting =
      bannerRect &&
      innerBannerRef.current &&
      magneticWallAbsoluteLeft &&
      bannerRect.right > magneticWallAbsoluteLeft;

    stateRef.current.restrictedSpans = isBannerIntersecting
      ? [
          VerticalSpan.fromDOMRect(
            getRelativeDOMRect(
              innerBannerRef.current.getBoundingClientRect(),
              containerRect,
            ),
          ),
          ...intersectingWiderLineSpans,
        ]
      : intersectingWiderLineSpans;

    updateLayout();
  };

  const debouncedUpdateDesiredPositions = useDebouncedCallback(
    updateDesiredPositions,
    DEBOUNCE_DELAY.Fast,
  );

  useSelectionChangeHandler((range) => {
    let node = getLowestCommonAncestorContainer(range);
    if (!node) return;

    if (!quill.root.contains(node)) {
      // If the selection is inside the embed selection container.
      const normalized = quill.selection.normalizeNative(range) as {
        start: { node: Node };
      } | null;
      if (normalized) {
        // The end node should always be the same as the start node.
        node = normalized.start.node;
      } else {
        // Ignore when the selection is not inside the editor.
        return;
      }
    }
    const element = node instanceof Element ? node : node.parentElement;

    const closestAttribute = element && getClosestAttribute(element);

    if (!closestAttribute || quill.root.contains(closestAttribute.node)) {
      if (
        closestAttribute?.id &&
        magneticWallFocus?.id !== closestAttribute.id
      ) {
        const type = getAttributorType(closestAttribute.attributor);
        const displayId = toDisplayId({
          id: closestAttribute.id,
          type,
        });
        const isVisible = visibleDisplayIds.has(displayId);
        if (!isVisible) return;

        dispatch(
          EditorActions.focusMagneticWall({
            id: closestAttribute.id,
            type,
            isActive: false,
            hasExpandIntent: false,
          }),
        );
      } else if (closestAttribute?.id == null && magneticWallFocus != null) {
        dispatch(EditorActions.blurMagneticWall());
      }
    }
  });

  useLayoutEffect(() => {
    stateRef.current.magneticWallItems = magneticWallDisplayItems;
    stateRef.current.magneticWallItemSize = magneticWallItemSize;

    updateLayout();
  }, [magneticWallDisplayItems, magneticWallItemSize, updateLayout]);

  useLayoutEffect(() => {
    stateRef.current.magneticWallFocus = magneticWallFocus;

    if (magneticWallFocus) {
      updateScrollingContainerVerticalBounds();
    }

    updateLayout();
  }, [magneticWallFocus, updateLayout, updateScrollingContainerVerticalBounds]);

  useEffect(() => {
    quill.on(Quill.events.TEXT_CHANGE, debouncedUpdateDesiredPositions);

    const removeUIElementResizeListener: () => void =
      addUIElementResizeListener(debouncedUpdateDesiredPositions);
    const unsubscribeCollapseStateListener =
      getHeaderCollapse(quill)?.subscribeToLocalCollapseStateChange(
        debouncedUpdateDesiredPositions,
      ) ?? (() => {});

    debouncedUpdateDesiredPositions();

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, debouncedUpdateDesiredPositions);

      removeUIElementResizeListener?.();
      unsubscribeCollapseStateListener();
    };
  }, [quill, debouncedUpdateDesiredPositions, addUIElementResizeListener]);

  useLayoutEffect(() => {
    if (!containerRef.current) return undefined;
    let lastWidth: number | null = null;
    const resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width } = entry.contentRect;
      if (width !== lastWidth && lastWidth !== null) {
        debouncedUpdateDesiredPositions();
      }
      lastWidth = width;
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [debouncedUpdateDesiredPositions]);

  const updateThreadHeights = useUpdateThreadHeights({
    stateRef,
    updatePositions: updateLayout,
  });

  useEffect(() => {
    updateThreadHeights();
  }, [threads, magneticWallFocus, magneticWallItemSize, updateThreadHeights]);

  useListenToPostScroll(
    useDebouncedCallback(() => {
      updateScrollingContainerVerticalBounds();
      updateLayout(true);
    }, DEBOUNCE_DELAY.Slow),
  );

  useEffect(() => {
    debouncedUpdateDesiredPositions();
    debouncedUpdateScrollingContainerVerticalBounds();
  }, [
    hasBanner,
    debouncedUpdateDesiredPositions,
    debouncedUpdateScrollingContainerVerticalBounds,
  ]);

  const focusedDisplayItem =
    magneticWallFocus &&
    magneticWallDisplayItems.find(
      (item) =>
        item.data.id === magneticWallFocus.id &&
        item.type === magneticWallFocus.type,
    );

  const isShowingFullSheetItem =
    magneticWallFocus?.active &&
    focusedDisplayItem != null &&
    showFullPageSheet();

  useEffect(() => {
    if (!isShowingFullSheetItem) {
      debouncedUpdateDesiredPositions();
    }
  }, [
    debouncedUpdateDesiredPositions,
    isShowingFullSheetItem,
    magneticWallFocus,
  ]);

  useScrollToThreadAnchor({
    skip: Object.keys(layout).length === 0,
  });

  return (
    <MainEditorResizeHandler
      onMainEditorResize={() => {
        debouncedUpdateScrollingContainerVerticalBounds();
        updateThreadHeights();
        debouncedUpdateDesiredPositions();
      }}
    >
      <div
        ref={containerRef}
        className="magnetic-wall"
        {...addTestId('magneticWall')}
        style={{
          minHeight: layout.magneticWallMinHeight ?? undefined,
        }}
      >
        <BaseMagneticWall
          post={post}
          magneticWallFocus={magneticWallFocus}
          onResize={updateThreadHeights}
          visibleMagneticWallDisplayItems={visibleMagneticWallDisplayItems}
          focusedDisplayItem={focusedDisplayItem ?? null}
          canEditPost={canEditPost}
          magneticWallItemSize={magneticWallItemSize}
        />
      </div>
    </MainEditorResizeHandler>
  );
};

// https://bugzilla.mozilla.org/show_bug.cgi?id=208427
function isFirefoxRestrictedNode(node: Node) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    node.parentNode;
  } catch (err) {
    return true;
  }

  return false;
}

export function getLowestCommonAncestorContainer(
  originalRange: Range,
): Node | null {
  if (
    isFirefoxRestrictedNode(originalRange.startContainer) ||
    isFirefoxRestrictedNode(originalRange.endContainer)
  ) {
    return null;
  }

  // To get the lowest common ancestor of the current range,
  // we narrow the range while keeping it visually identical.
  if (
    originalRange.collapsed ||
    originalRange.startContainer === originalRange.endContainer
  ) {
    // The range is already the narrowest one.
    return originalRange.commonAncestorContainer;
  }

  const range = originalRange.cloneRange();

  let sc: Node | null = range.startContainer;
  if (
    sc.nodeType === Node.TEXT_NODE &&
    range.startOffset === sc.nodeValue?.length
  ) {
    while (sc && !sc.nextSibling) {
      sc = sc.parentNode;
    }
    if (sc?.nextSibling) {
      range.setStart(sc.nextSibling, 0);
    }
  }

  if (range.endOffset === 0) {
    let ec: Node | null = range.endContainer;
    while (ec && !ec.previousSibling) {
      ec = ec.parentNode;
    }
    if (ec?.previousSibling) {
      const offset =
        ec.previousSibling.nodeType === Node.TEXT_NODE
          ? ec.previousSibling.nodeValue?.length
          : ec.previousSibling.childNodes?.length;
      if (typeof offset === 'number') {
        range.setEnd(ec.previousSibling, offset);
      }
    }
  }

  return range.commonAncestorContainer;
}

export default MagneticWall;
